import { Spin } from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styles from "./GapPercentEvolutionCard.module.css";

export interface GapPercentEvolutionCardProps {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  baseKey: string;
  compareKey: string;
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
}

interface GapPoint {
  period: string | number;
  gapPercent: number;
  baseValue: number;
  compareValue: number;
}

const getNumericValue = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value: number) => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1).replace(/\.0$/, "");
};

const formatPercent = (value: number) => {
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatValue(value)}%`;
};

const renderGapValueLabel = (props: unknown) => {
  const { x, y, width, height, value } = props as {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    value?: unknown;
  };

  if (
    x === undefined ||
    y === undefined ||
    width === undefined ||
    height === undefined ||
    typeof value !== "number"
  ) {
    return null;
  }

  const isPositive = value >= 0;
  const barTop = Math.min(y, y + height);
  const barBottom = Math.max(y, y + height);
  const labelX = x + width / 2;
  const labelY = isPositive ? barTop - 9 : barBottom + 14;

  return (
    <text
      x={labelX}
      y={labelY}
      textAnchor="middle"
      dominantBaseline={isPositive ? "baseline" : "hanging"}
      fill="#252B33"
      fontSize={10}
      fontWeight={800}
      pointerEvents="none"
    >
      {formatPercent(value)}
    </text>
  );
};

const buildGapData = (
  data: Record<string, string | number>[],
  xKey: string,
  baseKey: string,
  compareKey: string
): GapPoint[] => {
  return data
    .map((point) => {
      const baseValue = getNumericValue(point[baseKey]);
      const compareValue = getNumericValue(point[compareKey]);

      if (baseValue === null || compareValue === null || baseValue === 0) return null;

      return {
        period: point[xKey],
        baseValue,
        compareValue,
        gapPercent: Number((((compareValue - baseValue) / baseValue) * 100).toFixed(1)),
      };
    })
    .filter((point): point is GapPoint => point !== null);
};

const getReading = (gap: number) => {
  if (gap > 2) return "Catalunya per sobre d'Europa";
  if (gap < -2) return "Catalunya per sota d'Europa";
  return "Catalunya alineada amb Europa";
};

const getMomentumReading = (gapChange: number) => {
  if (gapChange > 2) return "La bretxa s'amplia a favor de Catalunya";
  if (gapChange < -2) return "La bretxa es redueix o empitjora";
  return "La bretxa es manté estable";
};

const GapPercentEvolutionCard = ({
  title,
  data,
  xKey,
  baseKey,
  compareKey,
  loading = false,
  error = null,
  headerExtra,
}: GapPercentEvolutionCardProps) => {
  const gapData = buildGapData(data, xKey, baseKey, compareKey);
  const firstPoint = gapData[0];
  const lastPoint = gapData[gapData.length - 1];
  const gapChange = firstPoint && lastPoint ? lastPoint.gapPercent - firstPoint.gapPercent : 0;
  const maxAbsGap = Math.max(10, ...gapData.map((point) => Math.abs(point.gapPercent)));

  return (
    <div className={styles.card} data-chart-card>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>
            Bretxa percentual de {compareKey} respecte a {baseKey}
          </p>
        </div>
        {headerExtra}
      </div>

      {loading && (
        <div className={styles.centered}>
          <Spin size="small" /> Carregant dades…
        </div>
      )}
      {!loading && error && (
        <div className={styles.centered}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
      {!loading && !error && gapData.length === 0 && (
        <div className={styles.centered}>
          <p className={styles.emptyMessage}>Sense dades disponibles</p>
        </div>
      )}
      {!loading && !error && gapData.length > 0 && lastPoint && (
        <>
          <div className={styles.insights}>
            <div className={styles.insight}>
              <span className={styles.insightLabel}>Bretxa actual</span>
              <strong className={lastPoint.gapPercent >= 0 ? styles.positive : styles.negative}>
                {formatPercent(lastPoint.gapPercent)}
              </strong>
              <span>{getReading(lastPoint.gapPercent)}</span>
            </div>
            <div className={styles.insight}>
              <span className={styles.insightLabel}>Canvi de bretxa</span>
              <strong className={gapChange >= 0 ? styles.positive : styles.negative}>
                {formatPercent(gapChange)}
              </strong>
              <span>{getMomentumReading(gapChange)}</span>
            </div>
            <div className={styles.insight}>
              <span className={styles.insightLabel}>Últim valor</span>
              <strong>{formatValue(lastPoint.compareValue)} vs {formatValue(lastPoint.baseValue)}</strong>
              <span>{compareKey} vs {baseKey}</span>
            </div>
          </div>

          <div className={styles.chartArea}>
            <ResponsiveContainer width="100%" height="100%" minHeight={0}>
              <BarChart
                data={gapData}
                margin={{ top: 38, right: 24, left: 4, bottom: 96 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF3" vertical={false} />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 12, fill: "#717C8B" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E8EDF3" }}
                  dy={44}
                />
                <YAxis
                  domain={[-maxAbsGap - 4, maxAbsGap + 4]}
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12, fill: "#717C8B" }}
                  tickLine={false}
                  axisLine={false}
                  width={64}
                />
                <ReferenceLine
                  y={0}
                  stroke="#64748B"
                  strokeDasharray="4 4"
                />
                <Tooltip
                  formatter={(value, _name, item) => {
                    const payload = item.payload as GapPoint;
                    return [
                      `${value}% · ${formatValue(payload.compareValue)} vs ${formatValue(payload.baseValue)}`,
                      `${compareKey} vs ${baseKey}`,
                    ];
                  }}
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E8EDF3",
                    fontSize: 13,
                  }}
                  cursor={{ fill: "#F8FAFC" }}
                />
                <Bar
                  dataKey="gapPercent"
                  name="Bretxa percentual"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={42}
                >
                  <LabelList
                    dataKey="gapPercent"
                    content={renderGapValueLabel}
                  />
                  {gapData.map((point) => (
                    <Cell
                      key={point.period}
                      fill={point.gapPercent >= 0 ? "#16A34A" : "#DC2626"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default GapPercentEvolutionCard;
